/** Building blocks for signed-in screens. Rounded, quiet, leaf green for actions. */

import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost";

const BUTTON: Record<Variant, string> = {
  primary: "bg-leaf-700 text-white hover:bg-leaf-800",
  secondary: "border border-stone-300 bg-white text-ink hover:bg-stone-50",
  danger: "border border-red-200 bg-white text-red-700 hover:bg-red-50",
  ghost: "text-leaf-700 hover:bg-leaf-50",
};

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" | "lg" }) {
  const sizing = size === "sm" ? "px-3 py-1.5 text-sm" : size === "lg" ? "px-6 py-3.5 text-base" : "px-4 py-2.5 text-[15px]";
  return (
    <button
      type="button"
      {...props}
      className={`rounded-xl font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${sizing} ${BUTTON[variant]} ${className}`}
    />
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-2xl border border-stone-200 bg-white p-6 ${className}`}>{children}</section>;
}

export function PageHeader({ title, detail, action }: { title: string; detail?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-semibold text-ink">{title}</h1>
        {detail ? <p className="mt-1 text-stone-600">{detail}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Notice({ tone = "error", children }: { tone?: "error" | "ok" | "info"; children: ReactNode }) {
  const styles =
    tone === "error" ? "bg-red-50 text-red-800" : tone === "ok" ? "bg-leaf-50 text-leaf-800" : "bg-stone-100 text-stone-700";
  return (
    <p role={tone === "error" ? "alert" : "status"} className={`rounded-xl px-4 py-3 text-sm ${styles}`}>
      {children}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-xl border border-dashed border-stone-300 px-4 py-8 text-center text-sm text-stone-500">{children}</p>;
}

export function Tag({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "warn" | "ok" | "bad" }) {
  const styles = {
    neutral: "bg-stone-100 text-stone-700",
    warn: "bg-amber-50 text-amber-800",
    ok: "bg-leaf-50 text-leaf-800",
    bad: "bg-red-50 text-red-800",
  }[tone];
  return <span className={`inline-block rounded-lg px-2 py-0.5 text-xs font-medium ${styles}`}>{children}</span>;
}

export const selectClass =
  "rounded-xl border border-stone-300 bg-white px-3 py-2.5 text-[15px] text-ink outline-none focus:border-leaf-600 focus:ring-2 focus:ring-leaf-100";

export const textareaClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-[15px] text-ink outline-none placeholder:text-stone-400 focus:border-leaf-600 focus:ring-2 focus:ring-leaf-100";
